import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Alert, Autocomplete, Box, Button, Chip, Paper, Skeleton, TextField, Typography,
} from '@mui/material';
import { useUser } from '../context/UserContext';
import { BACKEND_URL } from '../utils/constants';
import { colors } from '../theme';
import { GROUP_ORDER, byGroupThenName, groupFor } from '../components/MetricCharts';

/**
 * Dashboard → "Comparison" tab.
 *
 * Deals are COLUMNS, metrics are ROWS — the orientation a deal sheet is read
 * in, and the one that scales: adding a deal adds a column you scroll to,
 * while the metric names stay pinned in the frozen first column.
 *
 * The metric picker is built from the union of variable keys the SELECTED
 * deals actually carry, so you can never choose a row that renders empty for
 * everything. Coverage differs sharply by model type (development models carry
 * a Timeline and Sr. Cons block that acquisition models don't), which is why a
 * fixed master list would be mostly dashes.
 *
 * Values render exactly as the sheet stored them ("33.9%", "$1,234,567"). The
 * model already decided the units; re-formatting here risks misstating them.
 */

// --------------------------------------------------------------------------- //
// Types
// --------------------------------------------------------------------------- //
interface DealSummary {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  model_type: string | null;
  has_results: boolean;
}
interface CompareDeal {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  model_type: string | null;
  version_number: number | null;
  variables: Record<string, string | null>;
}

const MAX_DEALS = 12;

// Rows worth showing before anyone touches the picker: the numbers an IC memo
// leads with. Only those actually present on the chosen deals survive.
const DEFAULT_METRICS = [
  'Levered IRR',
  'Levered MOIC',
  'Going-in Cap Rate',
  'Year 1 NOI',
  'AQ: DSCR',
  'Implied Valuation at Exit',
  'Deal Time Horizon',
];

/**
 * A key starting with '=' is a broken Variable Mapping row — the sheet's
 * variable_name cell held the formula instead of a name, so the extractor
 * stored "=Cover!B13" as the metric's name. Never a real metric; hidden here
 * and counted so the omission isn't silent.
 */
const isBrokenKey = (k: string) => k.trim().startsWith('=');

const DashboardCompare: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useUser();

  const [summaries, setSummaries] = useState<DealSummary[]>([]);
  const [selected, setSelected] = useState<DealSummary[]>([]);
  const [deals, setDeals] = useState<CompareDeal[]>([]);
  const [metrics, setMetrics] = useState<string[]>(DEFAULT_METRICS);
  const [loading, setLoading] = useState(true);
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authedFetch = useCallback(async (path: string) => {
    const token = await getAccessTokenSilently({
      authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
    });
    const r = await fetch(`${BACKEND_URL}${path}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });
    if (!r.ok) throw new Error(`Request failed (${r.status})`);
    return r.json();
  }, [getAccessTokenSilently]);

  // ---- deal list for the picker ---- //
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) return;
      setLoading(true);
      setError(null);
      try {
        const rows: DealSummary[] = await authedFetch('/api/deals/summaries');
        if (!cancelled) setSummaries(rows);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not load your deals.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, authedFetch]);

  // ---- variables for the chosen deals ---- //
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (selected.length === 0) { setDeals([]); return; }
      setLoadingCompare(true);
      try {
        const ids = selected.map((s) => s.id).join(',');
        const res = await authedFetch(`/api/deals/compare?ids=${encodeURIComponent(ids)}`);
        if (!cancelled) setDeals(res.deals || []);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not load deal metrics.');
      } finally {
        if (!cancelled) setLoadingCompare(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected, authedFetch]);

  // Union of keys across the selected deals, minus the broken ones.
  const { available, brokenCount } = useMemo(() => {
    const set = new Set<string>();
    let broken = 0;
    deals.forEach((d) => Object.keys(d.variables || {}).forEach((k) => {
      if (isBrokenKey(k)) broken += 1; else set.add(k);
    }));
    // Array.from, not [...set] — the app targets es5 without downlevelIteration.
    const list = Array.from(set).sort(byGroupThenName);
    return { available: list, brokenCount: broken };
  }, [deals]);

  // Only show rows that exist on at least one selected deal — a row of pure
  // dashes teaches nothing and costs vertical space.
  const rows = useMemo(
    () => metrics.filter((m) => available.includes(m)),
    [metrics, available],
  );
  const droppedRows = metrics.length - rows.length;

  const downloadCsv = () => {
    const esc = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
    const head = ['Metric', ...deals.map((d) => d.name)].map(esc).join(',');
    const body = rows.map((m) =>
      [m, ...deals.map((d) => d.variables?.[m] ?? '')].map(esc).join(','));
    const blob = new Blob([[head, ...body].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'deal-comparison.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Skeleton variant="rounded" height={140} />
        <Skeleton variant="rounded" height={420} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => window.location.reload()}>Retry</Button>}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Pickers — deals first, then the rows to show for them */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Autocomplete
          multiple
          options={summaries}
          value={selected}
          onChange={(_, v) => setSelected(v.slice(0, MAX_DEALS))}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          getOptionDisabled={(o) => !o.has_results}
          filterSelectedOptions
          renderOption={(props, o) => (
            <Box component="li" {...props} key={o.id}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.name}</Typography>
                <Typography variant="caption" sx={{ color: colors.grey[600] }}>
                  {[o.model_type, [o.city, o.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                  {o.has_results ? '' : ' · no saved results'}
                </Typography>
              </Box>
            </Box>
          )}
          renderTags={(value, getTagProps) =>
            value.map((o, i) => (
              <Chip {...getTagProps({ index: i })} key={o.id} label={o.name} size="small" />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Deals to compare"
              placeholder={selected.length ? '' : 'Search by property name…'}
              helperText={`${selected.length} of up to ${MAX_DEALS} selected`}
            />
          )}
        />

        <Autocomplete
          multiple
          options={available}
          value={metrics}
          onChange={(_, v) => setMetrics(v)}
          groupBy={groupFor}
          disabled={deals.length === 0}
          filterSelectedOptions
          renderTags={(value, getTagProps) =>
            value.map((m, i) => (
              <Chip {...getTagProps({ index: i })} key={m} label={m} size="small" />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Metrics to show"
              placeholder={deals.length === 0 ? 'Pick deals first' : 'Add a metric…'}
              helperText={
                deals.length === 0
                  ? 'The list is built from the metrics your selected deals actually have.'
                  : `${available.length} metrics available across these deals`
                    + (brokenCount ? ` · ${brokenCount} unmapped variable${brokenCount === 1 ? '' : 's'} hidden` : '')
              }
            />
          )}
        />
      </Paper>

      {/* The comparison table */}
      {selected.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Pick deals to compare</Typography>
          <Typography variant="body2" color="text.secondary">
            Add two or more deals above. Each becomes a column; the metrics you choose become the rows.
          </Typography>
        </Paper>
      ) : loadingCompare ? (
        <Skeleton variant="rounded" height={420} />
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>No metrics selected</Typography>
          <Typography variant="body2" color="text.secondary">
            None of the chosen metrics exist on these deals. Add some from the list above.
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ color: colors.grey[600] }}>
              {rows.length} metric{rows.length === 1 ? '' : 's'} × {deals.length} deal{deals.length === 1 ? '' : 's'}
              {droppedRows > 0 && ` · ${droppedRows} hidden (not present on these deals)`}
            </Typography>
            <Button size="small" variant="outlined" onClick={downloadCsv}>Download CSV</Button>
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: 420 }}>
              <Box component="thead">
                <Box component="tr">
                  <Box
                    component="th"
                    sx={{
                      position: 'sticky', left: 0, zIndex: 2, bgcolor: colors.white,
                      textAlign: 'left', minWidth: 210,
                      p: 1.25, borderBottom: `2px solid ${colors.grey[300]}`,
                      borderRight: `1px solid ${colors.grey[300]}`,
                      color: colors.grey[600], fontSize: '0.6875rem', fontWeight: 700,
                      }}
                  >
                    Metric
                  </Box>
                  {deals.map((d) => (
                    <Box
                      component="th"
                      key={d.id}
                      sx={{
                        textAlign: 'right', minWidth: 150, p: 1.25,
                        borderBottom: `2px solid ${colors.grey[300]}`,
                        verticalAlign: 'bottom',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: colors.navy }}>
                        {d.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.grey[600], display: 'block' }}>
                        {[d.model_type, [d.city, d.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box component="tbody">
                {rows.map((m, ri) => (
                  <Box
                    component="tr"
                    key={m}
                    sx={{ '&:hover td, &:hover th': { bgcolor: colors.grey[50] } }}
                  >
                    <Box
                      component="th"
                      scope="row"
                      sx={{
                        position: 'sticky', left: 0, zIndex: 1,
                        bgcolor: ri % 2 ? colors.grey[100] : colors.white,
                        textAlign: 'left', p: 1.25,
                        borderBottom: `1px solid ${colors.grey[300]}`,
                        borderRight: `1px solid ${colors.grey[300]}`,
                        fontSize: '0.8125rem', fontWeight: 600, color: colors.grey[900],
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {m}
                    </Box>
                    {deals.map((d) => {
                      const v = d.variables?.[m];
                      return (
                        <Box
                          component="td"
                          key={d.id}
                          sx={{
                            textAlign: 'right', p: 1.25,
                            bgcolor: ri % 2 ? colors.grey[100] : colors.white,
                            borderBottom: `1px solid ${colors.grey[300]}`,
                            fontSize: '0.875rem',
                            fontVariantNumeric: 'tabular-nums',
                            color: v == null || v === '' ? colors.grey[400] : colors.grey[900],
                          }}
                        >
                          {v == null || v === '' ? '—' : v}
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: colors.grey[600] }}>
            Values come from each deal's latest saved version, exactly as the model produced them.
            A dash means that metric isn't captured for that deal — usually because its model type
            doesn't map it.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default DashboardCompare;
